import { X } from "@phosphor-icons/react";
import * as Dialog from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";

import { type OpellaVideo } from "../mockData";

interface VideoModalProps {
  video: OpellaVideo | null;
  onClose: () => void;
  onNavigate: (direction: "next" | "prev") => void;
}

export function VideoModal({ video, onClose, onNavigate }: VideoModalProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoFailed, setVideoFailed] = useState(false);

  const isOpen = video !== null;

  useEffect(() => {
    setVideoFailed(false);
  }, [video?.id]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        const el = videoRef.current;
        if (el) {
          try {
            el.pause();
            el.removeAttribute("src");
            el.load();
          } catch {
            // best-effort cleanup
          }
        }
        onClose();
      }
    },
    [onClose],
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      onNavigate("next");
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      onNavigate("prev");
    } else if (event.key === " ") {
      event.preventDefault();
      const el = videoRef.current;
      if (!el) return;
      if (el.paused) {
        void el.play().catch(() => {});
      } else {
        el.pause();
      }
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-50"
          style={{ background: "rgba(23, 18, 58, 0.72)" }}
        />
        <Dialog.Content
          className="opella-scope fixed left-1/2 top-1/2 z-50 w-[min(92vw,1080px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl p-6 outline-none"
          style={{
            background: "var(--opella-bg)",
            boxShadow: "var(--opella-shadow-elevated)",
          }}
          onKeyDown={handleKeyDown}
          aria-labelledby="opella-video-title"
        >
          {video ? (
            <>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p
                    className="text-xs font-semibold uppercase tracking-[0.12em]"
                    style={{ color: "var(--opella-primary)" }}
                  >
                    {video.brand}
                  </p>
                  <Dialog.Title
                    id="opella-video-title"
                    className="mt-1 truncate text-xl font-semibold"
                    style={{ color: "var(--opella-fg)" }}
                  >
                    {video.title}
                  </Dialog.Title>
                  <VisuallyHidden asChild>
                    <Dialog.Description>
                      {video.description ?? `${video.brand} — ${video.title}`}
                    </Dialog.Description>
                  </VisuallyHidden>
                </div>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    aria-label="Close"
                    className="flex size-10 shrink-0 items-center justify-center rounded-full"
                    style={{
                      background: "var(--opella-sidebar-bg)",
                      color: "var(--opella-fg-secondary)",
                    }}
                  >
                    <X size={18} weight="bold" aria-hidden />
                  </button>
                </Dialog.Close>
              </div>

              <div
                className="mt-5 overflow-hidden rounded-xl"
                style={{ background: "#000000" }}
              >
                {videoFailed ? (
                  <div
                    className="flex aspect-video items-center justify-center text-center"
                    style={{ color: "#ffffff" }}
                  >
                    <div className="max-w-sm px-6">
                      <p className="text-base font-semibold">
                        Asset unavailable
                      </p>
                      <p className="mt-2 text-sm opacity-80">
                        This clip couldn&apos;t be loaded. Upload a file to
                        <code
                          className="mx-1 rounded px-1"
                          style={{
                            background: "rgba(255,255,255,0.08)",
                            fontFamily:
                              "ui-monospace, SFMono-Regular, Menlo, monospace",
                          }}
                        >
                          {video.videoSrc}
                        </code>
                        then reopen.
                      </p>
                    </div>
                  </div>
                ) : (
                  <video
                    ref={videoRef}
                    key={video.id}
                    className="aspect-video w-full"
                    controls
                    muted
                    playsInline
                    autoPlay
                    preload="metadata"
                    src={video.videoSrc}
                    poster={video.thumbnailSrc}
                    onError={() => setVideoFailed(true)}
                  />
                )}
              </div>

              <div
                className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm"
                style={{ color: "var(--opella-muted)" }}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className="text-xs font-semibold uppercase tracking-wider"
                    style={{ color: "var(--opella-fg-secondary)" }}
                  >
                    Markets
                  </span>
                  {video.markets.map((market) => (
                    <span key={market} className="opella-pill">
                      {market}
                    </span>
                  ))}
                </div>
                <span className="tabular-nums">{video.duration}</span>
              </div>

              {video.description ? (
                <p
                  className="mt-3 text-sm leading-relaxed"
                  style={{ color: "var(--opella-fg-secondary)" }}
                >
                  {video.description}
                </p>
              ) : null}

              <p
                className="mt-4 text-xs"
                style={{ color: "var(--opella-muted)" }}
              >
                Use ← / → to browse, space to play / pause, Esc to close.
              </p>
            </>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
